import React, { useMemo, useState } from 'react';
import authService from '../../services/authService';

const categories = {
  early: 1,
  tech: 2,
  edu: 3,
  misc: 4
};

function deriveCategory(email) {
  try {
    const em = email.toLowerCase();
    if (!em.startsWith('testdrive') || !em.endsWith('@pocketpromptengineer.com')) return null;
    const between = em.slice('testdrive'.length, em.indexOf('@'));
    const n = parseInt(between, 10);
    if (Number.isNaN(n)) return null;
    if (n <= 9) return categories.early;
    if (n <= 79) return categories.tech;
    if (n <= 89) return categories.edu;
    return categories.misc;
  } catch { return null; }
}

const questionSets = {
  [categories.edu]: [
    'How easy was it to create effective prompts for lesson planning using PocketPE? (1 = Very difficult, 10 = Extremely easy)',
    'How much time did PocketPE save you compared to writing prompts from scratch? (1 = No time saved, 10 = Saved significant time)',
    'How well did the generated prompts help you create differentiated instruction materials? (1 = Not helpful at all, 10 = Extremely helpful)',
    'How clear and intuitive was the 6-step prompt creation process? (1 = Very confusing, 10 = Perfectly clear)',
    'How effectively did PocketPE help you generate prompts for student assessment creation? (1 = Not effective, 10 = Highly effective)',
    'How likely are you to recommend PocketPE to other teachers in your school? (1 = Would not recommend, 10 = Definitely recommend)',
    'How effectively did PocketPE help you generate prompts for creating course materials and assignments? (1 = Not effective, 10 = Highly effective)',
    'How satisfied are you with the quality of AI responses when using PocketPE-generated prompts? (1 = Very unsatisfied, 10 = Completely satisfied)',
    'How well does PocketPE fit into your daily teaching workflow? (1 = Doesn\'t fit at all, 10 = Integrates seamlessly)',
    'Overall, how valuable is PocketPE for your teaching practice? (1 = No value, 10 = Invaluable)'
  ],
  [categories.tech]: [
    "How well does PocketPE's prompt optimization improve upon your manual prompt engineering? (1 = No improvement, 10 = Significant improvement)",
    'How effectively does the chat mode handle complex, multi-turn prompt refinement workflows? (1 = Not effective, 10 = Extremely effective)',
    'How satisfied are you with the token efficiency of PocketPE-generated prompts compared to your hand-crafted ones? (1 = Much worse efficiency, 10 = Much better efficiency)',
    'How well does PocketPE scale for enterprise-level prompt engineering tasks? (1 = Doesn\'t scale, 10 = Scales excellently)',
    'How useful are the advanced customization options for fine-tuning prompt structure and parameters? (1 = Not useful, 10 = Essential)',
    'How effectively does PocketPE handle domain-specific technical prompts (coding, data analysis, etc.)? (1 = Poor handling, 10 = Excellent handling)',
    'How valuable is the conversation storage and retrieval system for maintaining prompt iteration history? (1 = No value, 10 = Critical feature)',
    'How well does PocketPE integrate with your existing AI development workflow and tools? (1 = Poor integration, 10 = Seamless integration)',
    'How satisfied are you with the ROI of your Pro subscription relative to time saved on prompt engineering? (1 = Poor ROI, 10 = Excellent ROI)',
    'How likely are you to continue using PocketPE as your primary prompt engineering platform? (1 = Will not continue, 10 = Definitely continuing)'
  ],
  [categories.early]: [
    "How much did PocketPE help you avoid common prompt engineering mistakes you didn't know you were making? (1 = No help, 10 = Saved me from many errors)",
    "How well did PocketPE's guided process teach you better prompting techniques? (1 = Learned nothing, 10 = Learned a lot)",
    'How confident do you feel about your prompt quality after using PocketPE versus before? (1 = No change in confidence, 10 = Much more confident)',
    'How well does PocketPE help you get consistent results instead of the hit-or-miss outcomes you had before? (1 = Still inconsistent, 10 = Very consistent now)',
    'How much did PocketPE reduce the trial-and-error time you used to spend tweaking prompts? (1 = No reduction, 10 = Massive time savings)',
    'How helpful were the professional templates in showing you what "good prompts" actually look like? (1 = Not helpful, 10 = Eye-opening)',
    'How well does PocketPE prevent you from over-complicating your prompts? (1 = Still over-complicate, 10 = Keeps me focused)',
    'How much has PocketPE improved your understanding of how to structure effective AI requests? (1 = No improvement, 10 = Much better understanding)',
    'How satisfied are you with getting professional-level results without needing deep technical expertise? (1 = Not satisfied, 10 = Very satisfied)',
    'How likely are you to recommend PocketPE to other "tech-curious" colleagues who struggle with prompting? (1 = Would not recommend, 10 = Definitely recommend)'
  ],
  [categories.misc]: [
    'How easy was PocketPE to use even without prior AI experience? (1 = Very difficult, 10 = Surprisingly easy)',
    'How well did PocketPE help you get better results from AI tools like ChatGPT or Claude? (1 = No improvement, 10 = Much better results)',
    'How clearly did the app explain what you needed to do at each step? (1 = Very confusing, 10 = Crystal clear)',
    'How useful was PocketPE for your specific personal or professional needs? (1 = Not useful, 10 = Extremely useful)',
    'How much did PocketPE reduce your frustration with getting AI to understand what you want? (1 = Still frustrated, 10 = Much less frustrated)',
    'How confident do you feel about using AI tools after trying PocketPE? (1 = Not confident, 10 = Very confident)',
    'How well did PocketPE work for the types of tasks you typically need help with? (1 = Poor fit, 10 = Perfect fit)',
    'How likely are you to continue using AI tools now that you\'ve experienced PocketPE? (1 = Very unlikely, 10 = Very likely)',
    'How would you rate the overall value PocketPE provides for someone like you? (1 = No value, 10 = Great value)',
    'How likely are you to tell friends or colleagues about PocketPE? (1 = Would not mention, 10 = Would definitely share)'
  ]
};

function getQuestionText(raw) {
  try {
    // Strip a trailing parenthetical anchor like "(1 = ..., 10 = ...)" if present
    return (raw || '').replace(/\s*\([^)]*\)\s*$/, '');
  } catch {
    return raw;
  }
}

function getQuestionAnchor(raw) {
  try {
    const m = /\s*\(([^)]*)\)\s*$/.exec(raw || '');
    return m ? m[1] : '1 = Very low, 10 = Very high';
  } catch {
    return '1 = Very low, 10 = Very high';
  }
}

function parseAnchorSides(raw) {
  try {
    const anchor = getQuestionAnchor(raw);
    const leftMatch = anchor.match(/1\s*=\s*([^,]+)/);
    const rightMatch = anchor.match(/10\s*=\s*(.+)$/);
    const left = leftMatch ? leftMatch[1].trim() : 'Very low';
    const right = rightMatch ? rightMatch[1].trim() : 'Very high';
    return { left, right };
  } catch {
    return { left: 'Very low', right: 'Very high' };
  }
}

const FeedbackForm = ({ user }) => {
  const email = (user?.email || user?.profile?.email || user?.name || '').toString();
  const categoryId = useMemo(() => deriveCategory(email), [email]);
  const questions = questionSets[categoryId] || [];
  const [answers, setAnswers] = useState(() => Array(10).fill(null));
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => answers.every(v => typeof v === 'number' && (v === 99 || (v >= 1 && v <= 10))), [answers]);

  const setAnswer = (idx, val) => {
    setAnswers(prev => prev.map((v, i) => i===idx ? val : v));
  };

  const onSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      const t = authService.getToken();
      const body = {
        CategoryId: categoryId,
        Q1: answers[0], Q2: answers[1], Q3: answers[2], Q4: answers[3], Q5: answers[4], Q6: answers[5], Q7: answers[6], Q8: answers[7], Q9: answers[8], Q10: answers[9],
        Comment: comment
      };
      const res = await fetch('/api/feedback', { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${t}` }, body: JSON.stringify(body) });
      if (res.ok) {
        setAnswers(Array(10).fill(null));
        setComment('');
        alert('Feedback submitted. Thank you!');
      } else {
        let msg = 'Failed to submit feedback.';
        try {
          const err = await res.json();
          if (err?.message) msg = `${msg} ${err.message}`;
          if (Array.isArray(err?.errors) && err.errors.length) msg = `${msg} ${err.errors.join('; ')}`;
        } catch {}
        alert(msg);
      }
    } finally { setSaving(false); }
  };

  const renderScale = (idx) => (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(10, auto)', columnGap:20, alignItems:'center' }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <label key={i} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
            <input type="radio" name={`q${idx}`} checked={answers[idx] === (i+1)} onChange={()=>setAnswer(idx, i+1)} /> {i+1}
          </label>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(10, auto)', columnGap:20, marginTop:6, fontSize:'0.9em', color:'#555' }}>
        {/* guidance labels will be injected by parent */}
      </div>
      <div style={{ marginTop:6 }}>
        <label>
          <input type="radio" name={`q${idx}`} checked={answers[idx] === 99} onChange={()=>setAnswer(idx, 99)} /> N/A
        </label>
      </div>
    </div>
  );

  const title = categoryId===categories.edu ? 'Education' : categoryId===categories.tech ? 'Techies' : categoryId===categories.early ? 'Early Adopters' : 'Miscellaneous';

  return (
    <div className="dashboard-card">
      <h3 style={{ marginTop: 0 }}>{title} Feedback</h3>
      {questions.map((q, idx) => (
        <div key={idx} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight:600, marginBottom:6 }}>{idx+1}. {getQuestionText(q)}</div>
          {/* scale row */}
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(11, auto)', columnGap:20, alignItems:'center' }}>
              {Array.from({ length: 10 }).map((_, i) => (
                <label key={i} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  <input type="radio" name={`q${idx}`} checked={answers[idx] === (i+1)} onChange={()=>setAnswer(idx, i+1)} /> {i+1}
                </label>
              ))}
              <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                <input type="radio" name={`q${idx}`} checked={answers[idx] === 99} onChange={()=>setAnswer(idx, 99)} /> N/A
              </label>
            </div>
            {/* guidance under 1 and 10 */}
            {(() => { const sides = parseAnchorSides(q); return (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(11, auto)', columnGap:20, marginTop:6, fontSize:'0.9em', color:'#555' }}>
                <span style={{ gridColumn:'1 / 2' }}>{sides.left}</span>
                <span style={{ gridColumn:'10 / 11', textAlign:'right' }}>{sides.right}</span>
              </div>
            ); })()}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 12 }}>
        <label style={{ display:'block', fontWeight:600, marginBottom:6 }}>Additional comments (optional)</label>
        <textarea value={comment} onChange={e=>setComment(e.target.value)} style={{ width:'100%', minHeight:100 }} />
      </div>
      <div className="admin-controls" style={{ gap:8, marginTop: 12, justifyContent:'flex-end' }}>
        <button onClick={()=>{ setAnswers(Array(10).fill(null)); setComment(''); }}>Cancel</button>
        <button className="primary" disabled={!canSave || saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};

export default FeedbackForm;


